/**
 * Event types mirror the backend (`apps/api/src/SSE/events.ts`).
 *
 * Keep these in sync — both files must agree on the wire format.
 *
 * Two extra helpers at the bottom (`isUserEvent`, `eventType`) make
 * narrowing painless in TypeScript without pulling in a schema library.
 */

export type ServerState =
  | 'starting'
  | 'running'
  | 'restarting'
  | 'stopping'
  | 'stopped'
  | 'crashed';

export type PaymentMethod = 'paypal' | 'oxapay' | 'manual' | 'referral' | 'transfer';

export interface ServerSummary {
  id: string;
  name: string;
  game: string;
  type: string;
  version: string;
  active: boolean;
  ip: string | null;
  /**
   * Permission scope of the recipient user over this server.
   * 'owner' = creator / full control; 'on/off' = delegated start/stop only.
   * Required so the Dashboard's `s.permissions === 'owner'` filter matches
   * the freshly-added server when SSE-patched.
   */
  permissions: 'owner' | 'on/off';
  /**
   * ISO 8601 creation timestamp. Used by the Dashboard to keep the list
   * sorted newest-first even after SSE-patches inject a new entry.
   */
  created_at?: string;
}

export interface NotificationSummary {
  id: string;
  title: string;
  body: string;
  created_at: string;
  read: boolean;
}

export interface SessionSummary {
  server_id: string;
  seconds_active: number;
  cost: number;
  ended_at: string;
}

export type UserEvent =
  | { type: 'server.created'; server: ServerSummary }
  | { type: 'server.deleted'; server_id: string }
  | {
      type: 'server.state_changed';
      server_id: string;
      state: ServerState;
      ip?: string | null;
    }
  | {
      /**
       * Non-state config changes (plan, version, software type, ftp_password,
       * permissions, etc). The `fields` array hints at what changed so the
       * frontend can decide whether to refetch the whole server or just
       * patch a slice. Empty array means "something changed, refetch".
       */
      type: 'server.updated';
      server_id: string;
      fields: ('plan_id' | 'version' | 'type' | 'ftp_password' | 'permissions' | 'name')[];
    }
  | { type: 'cash.updated'; new_cash: number; delta: number }
  | { type: 'notification.new'; notification: NotificationSummary }
  | { type: 'session.ended'; summary: SessionSummary }
  | { type: 'payment.received'; amount: number; method: PaymentMethod };

export type UserEventType = UserEvent['type'];

export function eventType(e: UserEvent): UserEventType {
  return e.type;
}

/**
 * Type guard for raw JSON values coming off the wire. Tolerates unknown
 * `type` strings (we just drop them).
 */
export function isUserEvent(value: unknown): value is UserEvent {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { type?: unknown }).type;
  return (
    t === 'server.created' ||
    t === 'server.deleted' ||
    t === 'server.state_changed' ||
    t === 'server.updated' ||
    t === 'cash.updated' ||
    t === 'notification.new' ||
    t === 'session.ended' ||
    t === 'payment.received'
  );
}